import { Button, Flex, Typography } from "djuno-design";
import GoogleIcon from "./../../assets/icons/socials/google-color.svg?react";
import MicrosoftIcon from "./../../assets/icons/socials/microsoft-color.svg?react";
import GitHubIcon from "./../../assets/icons/socials/github-color.svg?react";
import { createContext, useCallback, useContext, useState } from "react";
import {
  loginWithGithubAsync,
  loginWithGoogleAsync,
  loginWithMicrosoftAsync,
} from "../../store/auth/authSlice";
import { SignInUrl } from "../../utils/urls";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../hooks";
import { LoginUser } from "../../types/auth";

export type SocialSigninType =
  | "signin-google"
  | "signin-microsoft"
  | "signin-github";

export type SocialSigninTypeEnum =
  | 0 //"signin-google"
  | 1 //"signin-microsoft"
  | 2; //"signin-github";

export type SocialSigninState = {
  socialLoading: boolean;

  handleLoginWithGoogle: () => void;
  handleBackFromGoogle: (props: {
    callback: (login: LoginUser, state: string | null) => void;
  }) => void;

  handleLoginWithMicrosoft: () => void;
  handleBackFromMicrosoft: (props: {
    callback: (login: LoginUser, state: string | null) => void;
  }) => void;

  handleLoginWithGithub: () => void;
  handleBackFromGithub: (props: {
    callback: (login: LoginUser, state: string | null) => void;
  }) => void;

  accessToken: null | string;
  setAccessToken: (accessToken: null | string) => void;
};

const SocialSigninContext = createContext<SocialSigninState | null>(null);

const SocialsSignInProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState<null | string>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // --- google login
  const handleLoginWithGoogle = useCallback(() => {
    const urlSearchData = location.search;

    // Google's OAuth 2.0 endpoint for requesting an access token
    const oauth2Endpoint = "https://accounts.google.com/o/oauth2/v2/auth";

    // Create <form> element to submit parameters to OAuth 2.0 endpoint.
    const form = document.createElement("form");
    form.setAttribute("method", "GET"); // Send as a GET request.
    form.setAttribute("action", oauth2Endpoint);

    // Parameters to pass to OAuth 2.0 endpoint.
    const params = {
      client_id: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || "",
      redirect_uri: window.location.origin + "/auth/signin-google",
      response_type: "token",
      scope: "openid email profile",
      include_granted_scopes: "true",
      state: urlSearchData,
    };

    // Add form parameters as hidden input values.
    for (const p in params) {
      if (Object.prototype.hasOwnProperty.call(params, p)) {
        const input = document.createElement("input");
        input.setAttribute("type", "hidden");
        input.setAttribute("name", p);
        input.setAttribute("value", params[p as keyof typeof params]);
        form.appendChild(input);
      }
    }

    // Add form to page and submit it to open the OAuth 2.0 endpoint.
    document.body.appendChild(form);
    form.submit();
  }, [location.search]);

  const handleBackFromGoogle = useCallback(
    ({
      callback,
    }: {
      callback: (login: LoginUser, state: string | null) => void;
    }) => {
      setLoading(true);
      const hash = window.location.hash;
      if (hash) {
        const hashParams = new URLSearchParams(hash.replace("#", "?"));
        const access_token = hashParams.get("access_token");
        const token_type = hashParams.get("token_type");
        const expires_in = hashParams.get("expires_in");
        const scope = hashParams.get("scope");
        const state = hashParams.get("state");
        const error = hashParams.get("error"); // http://localhost:3000/auth/signin-google#error=access_denied

        const stateReturnUrl = state?.split("?return-url=").filter(Boolean)[0];
        const nextLink =
          SignInUrl + (stateReturnUrl ? `?return-url=${stateReturnUrl}` : "");

        if (error !== null || !access_token) {
          setLoading(false);
          navigate(nextLink);
          return;
        }

        dispatch(
          loginWithGoogleAsync({ access_token, token_type, expires_in, scope })
        ).then((action) => {
          setLoading(false);
          if (action.type === "auth/login-with-google/fulfilled") {
            const { login } = action.payload as { login: LoginUser };
            setAccessToken(access_token);
            callback(login, state);
          } else {
            navigate(nextLink);
          }
        });
      } else {
        navigate(SignInUrl);
      }
    },
    [dispatch, navigate]
  );

  // microsoft login
  const handleLoginWithMicrosoft = useCallback(() => {
    const urlSearchData = location.search;

    const endpoint =
      "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
    const form = document.createElement("form");
    form.setAttribute("method", "GET");
    form.setAttribute("action", endpoint);

    const params = {
      client_id: import.meta.env.VITE_MICROSOFT_OAUTH_CLIENT_ID || "",
      response_type: "code",
      response_mode: "query",
      redirect_uri: window.location.origin + "/auth/signin-microsoft",
      scope: "user.read mail.read",
      state: urlSearchData,
    };

    for (const p in params) {
      const input = document.createElement("input");
      input.setAttribute("type", "hidden");
      input.setAttribute("name", p);
      input.setAttribute("value", params[p as keyof typeof params]);
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }, [location.search]);

  const handleBackFromMicrosoft = useCallback(
    ({
      callback,
    }: {
      callback: (login: LoginUser, state: string | null) => void;
    }) => {
      setLoading(true);
      const search = window.location.search;

      if (search) {
        const searchParams = new URLSearchParams(search);
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");

        const stateReturnUrl = state?.split("?return-url=").filter(Boolean)[0];
        const nextLink =
          SignInUrl + (stateReturnUrl ? `?return-url=${stateReturnUrl}` : "");

        if (error !== null || !code) {
          setLoading(false);
          navigate(nextLink);
          return;
        }

        dispatch(
          loginWithMicrosoftAsync({
            code,
          })
        ).then((action) => {
          setLoading(false);
          if (action.type === "auth/login-with-microsoft/fulfilled") {
            const { login } = action.payload as { login: LoginUser };
            setAccessToken(code);
            callback(login, state);
          } else {
            navigate(nextLink);
          }
        });
      } else {
        navigate(SignInUrl);
      }
    },
    [dispatch, navigate]
  );

  // github login
  const handleLoginWithGithub = useCallback(() => {
    const urlSearchData = location.search;

    const endpoint = "https://github.com/login/oauth/authorize";
    const form = document.createElement("form");
    form.setAttribute("method", "GET");
    form.setAttribute("action", endpoint);

    const params = {
      client_id: import.meta.env.VITE_GITHUB_OAUTH_CLIENT_ID || "",
      redirect_uri: window.location.origin + "/auth/signin-github",
      scope: "read:user user:email",
      state: urlSearchData,
    };

    for (const p in params) {
      const input = document.createElement("input");
      input.setAttribute("type", "hidden");
      input.setAttribute("name", p);
      input.setAttribute("value", params[p as keyof typeof params]);
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }, [location.search]);

  const handleBackFromGithub = useCallback(
    ({
      callback,
    }: {
      callback: (login: LoginUser, state: string | null) => void;
    }) => {
      setLoading(true);

      const search = window.location.search;
      if (search) {
        const params = new URLSearchParams(search);

        const code = params.get("code");
        const state = params.get("state");
        const error = params.get("error");

        const stateReturnUrl = state?.split("?return-url=").filter(Boolean)[0];
        const nextLink =
          SignInUrl + (stateReturnUrl ? `?return-url=${stateReturnUrl}` : "");

        if (error !== null || !code) {
          setLoading(false);
          navigate(nextLink);
          return;
        }

        dispatch(loginWithGithubAsync({ code })).then((action) => {
          setLoading(false);
          if (action.type === "auth/login-with-github/fulfilled") {
            const { login } = action.payload as { login: LoginUser };
            setAccessToken(code);
            callback(login, state);
          } else {
            navigate(nextLink);
          }
        });
      } else {
        navigate(SignInUrl);
      }
    },
    [dispatch, navigate]
  );

  return (
    <SocialSigninContext.Provider
      value={{
        socialLoading: loading,
        handleLoginWithGoogle,
        handleBackFromGoogle,
        handleLoginWithMicrosoft,
        handleBackFromMicrosoft,
        handleLoginWithGithub,
        handleBackFromGithub,
        accessToken,
        setAccessToken,
      }}
    >
      {children}
    </SocialSigninContext.Provider>
  );
};

export const useSocialSignin = () => {
  const context = useContext(SocialSigninContext);
  if (!context) {
    throw new Error(
      "useSocialSignin must be used within a SocialsSignInProvider"
    );
  }
  return context;
};

export const SocialsSignInButtons: React.FC = () => {
  const {
    handleLoginWithGoogle,
    handleLoginWithMicrosoft,
    handleLoginWithGithub,
  } = useSocialSignin();

  return (
    <Flex direction="row" className="gap-3">
      <Button
        uiType="light"
        onClick={handleLoginWithGoogle}
        className="!w-full"
      >
        <Flex className="w-full items-center justify-center gap-2">
          <GoogleIcon className="w-5 h-5 aspect-square flex-shrink-0" />
          <Typography.Text size="sm" className="hidden md:block">
            Google
          </Typography.Text>
        </Flex>
      </Button>

      <Button
        uiType="light"
        onClick={handleLoginWithMicrosoft}
        className="!w-full"
      >
        <Flex className="w-full items-center justify-center gap-2">
          <MicrosoftIcon className="w-5 h-5 aspect-square flex-shrink-0" />
          <Typography.Text size="sm" className="hidden md:block">
            Microsoft
          </Typography.Text>
        </Flex>
      </Button>

      <Button
        uiType="light"
        onClick={handleLoginWithGithub}
        className="!w-full"
      >
        <Flex className="w-full items-center justify-center gap-2">
          <GitHubIcon className="w-5 h-5 aspect-square flex-shrink-0 text-slate-600 dark:!text-slate-300" />
          <Typography.Text size="sm" className="hidden md:block">
            GitHub
          </Typography.Text>
        </Flex>
      </Button>
    </Flex>
  );
};

export default SocialsSignInProvider;
